import { makeAutoObservable } from "mobx";

import { ImageQuality, ImageSize, SupportedAiModelResponse } from "../../@api";

export interface IThumbnailsMap {
  [imageId: string]: Blob;
}

export interface ILoadingMap {
  loadingImageIds: boolean;
  loadingThumbnails: boolean;
}

export interface IProcessMap {
  processId?: string;
  generatedImageIds: string[];
  thumbnailsMap?: IThumbnailsMap;
}

export interface IImageGenerationSettings {
  prompt: string;
  imageCount: number;
  imageResolution: ImageSize;
  imageQuality: ImageQuality;
  backgroundColor?: string;
  selectedAiModel?: SupportedAiModelResponse;
}

export class ImageGenerationStore {
  private _generatingImages: boolean = false;

  private _loading: ILoadingMap = {
    loadingImageIds: false,
    loadingThumbnails: false,
  };
  private _settings: IImageGenerationSettings = {
    prompt: "",
    imageCount: 1,
    imageResolution: ImageSize.Auto,
    imageQuality: ImageQuality.Auto,
    backgroundColor: undefined,
    selectedAiModel: undefined,
  };
  private _activeProcess: IProcessMap | undefined;

  constructor() {
    makeAutoObservable(this, {}, { autoBind: true });
  }

  public get isGeneratingImages(): boolean {
    return this._generatingImages;
  }

  public setGeneratingImages(generatingImages: boolean): void {
    this._generatingImages = generatingImages;
  }

  // LoadingImageIds, LoadingThumbnails and general Loading state
  public get loadingImageIds(): boolean {
    return this._loading.loadingImageIds;
  }

  public get loadingThumbnails(): boolean {
    return this._loading.loadingThumbnails;
  }

  public setLoadingImageIds(loading: boolean): void {
    this._loading.loadingImageIds = loading;
  }

  public setLoadingThumbnails(loading: boolean): void {
    this._loading.loadingThumbnails = loading;
  }

  // Prompt area and its Settings
  public get prompt(): string {
    return this._settings.prompt;
  }

  public get imageCount(): number {
    return this._settings.imageCount;
  }

  public get imageResolution(): ImageSize {
    return this._settings.imageResolution;
  }

  public get imageQuality(): ImageQuality {
    return this._settings.imageQuality;
  }

  public get selectedAiModel(): SupportedAiModelResponse | undefined {
    return this._settings.selectedAiModel;
  }

  public get backgroundColor(): string | undefined {
    return this._settings.backgroundColor;
  }

  public get settings(): IImageGenerationSettings {
    return this._settings;
  }
  public setPrompt(prompt: string): void {
    this._settings.prompt = prompt;
  }

  public setImageCount(imageCount: number): void {
    this._settings.imageCount = imageCount;
  }

  public setImageResolution(imageResolution: ImageSize): void {
    this._settings.imageResolution = imageResolution;
  }

  public setImageQuality(imageQuality: ImageQuality): void {
    this._settings.imageQuality = imageQuality;
  }

  public setSelectedAiModel(selectedAiModel: SupportedAiModelResponse | undefined): void {
    this._settings.selectedAiModel = selectedAiModel;
  }

  public setBackgroundColor(backgroundColor?: string): void {
    this._settings.backgroundColor = backgroundColor;
  }

  // ProcessId and Images
  public get activeProcess(): IProcessMap | undefined {
    return this._activeProcess;
  }

  public get generatedImageIds(): string[] {
    return this._activeProcess?.generatedImageIds ?? [];
  }

  public get thumbnailsMap(): IThumbnailsMap | undefined {
    return this._activeProcess?.thumbnailsMap;
  }

  public resetActiveProcess(): void {
    this._activeProcess = undefined;
    this.setGeneratingImages(false);
    this.setLoadingImageIds(false);
    this.setLoadingThumbnails(false);
  }

  public setActiveProcessId(processId: string): void {
    this._activeProcess = {
      ...this._activeProcess,
      processId,
      generatedImageIds: [],
    };
  }

  public setGeneratedImageIds(generatedImageIds: string[]): void {
    if (!this._activeProcess?.processId) {
      console.error("[ImageGenerationStore] No active image generation process exist");
      return;
    }

    this._activeProcess.generatedImageIds = generatedImageIds;
  }

  public setThumbnailMapToActiveProcess(thumbnailsMap: IThumbnailsMap): void {
    if (!this._activeProcess?.processId) {
      console.error("[ImageGenerationStore] No active image generation process exist");
      return;
    }

    this._activeProcess.thumbnailsMap = thumbnailsMap;
  }
}
